import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
// Базовый URL сервера (без /api/v1)
export const SERVER_BASE_URL = API_BASE_URL.replace('/api/v1', '');

// Создаем экземпляр axios
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Добавляем токен к каждому запросу
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Обработка ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    return api.post('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
  },
};

export const channelsAPI = {
  getAll: () => api.get('/admin/channels'),
  getById: (id) => api.get(`/admin/channels/${id}`),
  create: (channel) => api.post('/admin/channels', channel),
  update: (id, channel) => api.put(`/admin/channels/${id}`, channel),
  delete: (id) => api.delete(`/admin/channels/${id}`),
  swap: (channel1_id, channel2_id) => api.post('/admin/channels/swap', null, {
    params: { channel1_id, channel2_id }
  }),
  normalize: () => api.post('/admin/channels/normalize'),

  uploadIcon: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/channels/upload-icon', formData);
  },
};

export const playlistAPI = {
  get: () => api.get('/playlist'),
};

export const configAPI = {
  get: () => api.get('/admin/config')
};

export const applicationsAPI = {
  getAll: () => api.get('/admin/applications'),
  getById: (id) => api.get(`/admin/applications/${id}`),
  create: (application) => api.post('/admin/applications', application),
  update: (id, application) => api.put(`/admin/applications/${id}`, application),
  delete: (id) => api.delete(`/admin/applications/${id}`),
};

// Playlists API
export const playlistsAPI = {
  getAll: () => api.get('/admin/playlists'),
  getById: (id) => api.get(`/admin/playlists/${id}`),
  create: (playlist) => api.post('/admin/playlists', playlist),
  update: (id, playlist) => api.put(`/admin/playlists/${id}`, playlist),
  delete: (id) => api.delete(`/admin/playlists/${id}`),
  getChannels: (id) => api.get(`/admin/playlists/${id}/channels`),
  getChannel: (playlistId, channelId) => api.get(`/admin/playlists/${playlistId}/channels/${channelId}`),
  addChannel: (playlistId, channelId, sortOrder = 0) => 
    api.post(`/admin/playlists/${playlistId}/channels/${channelId}`, { sort_order: sortOrder }),
  removeChannel: (playlistId, channelId) => 
    api.delete(`/admin/playlists/${playlistId}/channels/${channelId}`),
  updateChannel: (playlistId, channelId, channelData) => 
    api.put(`/admin/playlists/${playlistId}/channels/${channelId}`, channelData),
  swapChannels: (playlistId, channel1Id, channel2Id) => 
    api.post(`/admin/playlists/${playlistId}/channels/swap?channel1_id=${channel1Id}&channel2_id=${channel2Id}`),
  moveChannel: (playlistId, channelId, newPosition) =>
    api.post(`/admin/playlists/${playlistId}/channels/move?channel_id=${channelId}&new_position=${newPosition}`),
  reorderChannels: (playlistId, channelOrders) => {
    // Если это move (для drag-and-drop) - передаём channel_id и new_position
    if (channelOrders.channel_id !== undefined && channelOrders.new_position !== undefined) {
      return api.post(`/admin/playlists/${playlistId}/channels/move?channel_id=${channelOrders.channel_id}&new_position=${channelOrders.new_position}`);
    }
    // Если это swap двух каналов (для стрелочек)
    if (channelOrders.channel1_id && channelOrders.channel2_id) {
      return api.post(`/admin/playlists/${playlistId}/channels/swap?channel1_id=${channelOrders.channel1_id}&channel2_id=${channelOrders.channel2_id}`);
    }
    // Иначе используем старый endpoint для массового переупорядочивания
    return api.post(`/admin/playlists/${playlistId}/reorder`, channelOrders);
  },
};

export const adsAPI = {
  getAll: () => api.get('/admin/ads'),
  getById: (id) => api.get(`/admin/ads/${id}`),
  create: (data) => api.post('/admin/ads', data),
  update: (id, data) => api.put(`/admin/ads/${id}`, data),
  delete: (id) => api.delete(`/admin/ads/${id}`),
  getByType: (adType) => api.get(`/admin/ads/by-type/${adType}`),
  toggle: (id) => api.post(`/admin/ads/toggle/${id}`),
};

export const globalAdsSettingsAPI = {
  get: () => api.get('/admin/global-ads-settings'),
  update: (data) => api.put('/admin/global-ads-settings', data),
};

// Radio API
export const radioAPI = {
  // Radio playlists
  getPlaylists: () => api.get('/admin/radio/playlists'),
  getPlaylistById: (id) => api.get(`/admin/radio/playlists/${id}`),
  createPlaylist: (playlist) => api.post('/admin/radio/playlists', playlist),
  updatePlaylist: (id, playlist) => api.put(`/admin/radio/playlists/${id}`, playlist),
  deletePlaylist: (id) => api.delete(`/admin/radio/playlists/${id}`),
  
  // Radio channels in playlists
  getPlaylistChannels: (playlistId) => api.get(`/admin/radio/playlists/${playlistId}/channels`),
  getPlaylistChannel: (playlistId, channelId) => api.get(`/admin/radio/playlists/${playlistId}/channels/${channelId}`),
  addChannelToPlaylist: (playlistId, channelId, sortOrder = 0) => 
    api.post(`/admin/radio/playlists/${playlistId}/channels/${channelId}`, { sort_order: sortOrder }),
  removeChannelFromPlaylist: (playlistId, channelId) => 
    api.delete(`/admin/radio/playlists/${playlistId}/channels/${channelId}`),
  updateChannelInPlaylist: (playlistId, channelId, channelData) => 
    api.put(`/admin/radio/playlists/${playlistId}/channels/${channelId}`, channelData),
  reorderChannels: (playlistId, reorderData) => 
    api.post(`/admin/radio/playlists/${playlistId}/reorder`, reorderData),
  
  // Radio channels CRUD
  getChannels: () => api.get('/admin/radio/channels'),
  getChannelById: (id) => api.get(`/admin/radio/channels/${id}`),
  createChannel: (channel) => api.post('/admin/radio/channels', channel),
  updateChannel: (id, channel) => api.put(`/admin/radio/channels/${id}`, channel),
  deleteChannel: (id) => api.delete(`/admin/radio/channels/${id}`),
  
  // Upload radio channel icon
  uploadIcon: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/radio/channels/upload-icon', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Public radio API
  getPublicRadio: (appBundle) => {
    return api.get('/radio', {
      headers: { 'app': JSON.stringify({ bundle: appBundle }) }
    });
  },
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/admin/users'),
  getById: (id) => api.get(`/admin/users/${id}`),
  create: (user) => api.post('/admin/users', user),
  update: (id, user) => api.put(`/admin/users/${id}`, user),
  delete: (id) => api.delete(`/admin/users/${id}`),
  getActivity: (id) => api.get(`/admin/users/${id}/activity`),
};

// Admin Logs API
export const logsAPI = {
  getAll: (params) => api.get('/admin/logs', { params }),
  getCount: (params) => api.get('/admin/logs/count', { params }),
  getFilters: () => api.get('/admin/logs/filters'),
};

export const monitoringAPI = {
  getSystemMetrics: () => api.get('/admin/monitoring/system/metrics'),
  getRequestLogs: (limit = 200) => api.get('/admin/monitoring/requests/logs', { params: { limit } }),
  getRequestStatistics: () => api.get('/admin/monitoring/requests/statistics'),
  getAppStatistics: () => api.get('/admin/monitoring/requests/apps'),
  clearLogs: () => api.post('/admin/monitoring/requests/clear'),
  getCacheStats: () => api.get('/admin/cache/stats'),
  
  // Security endpoints
  getSecurityStats: () => api.get('/admin/monitoring/security/stats'),
  getBlockedIps: (skip = 0, limit = 100) => api.get('/admin/monitoring/security/blocked-ips', { params: { skip, limit } }),
  getWhitelistedIps: () => api.get('/admin/monitoring/security/whitelisted-ips'),
  getSuspiciousIps: () => api.get('/admin/monitoring/security/suspicious-ips'),
  blockIp: (ip, reason = 'Manual block') => api.post('/admin/monitoring/security/block-ip', { ip, reason }),
  unblockIp: (ip) => api.post('/admin/monitoring/security/unblock-ip', { ip }),
  addToWhitelist: (ip) => api.post('/admin/monitoring/security/whitelist-add', { ip }),
  removeFromWhitelist: (ip) => api.post('/admin/monitoring/security/whitelist-remove', { ip }),
  clearSuspicious: (ip) => api.post('/admin/monitoring/security/clear-suspicious', { ip }),
  updateBlockedIp: (ip, reason, blocked_by) => api.put('/admin/monitoring/security/update-blocked-ip', { ip, reason, blocked_by }),
  bulkUnblock: (ip_addresses) => api.post('/admin/monitoring/security/bulk-unblock', { ip_addresses }),
  bulkBlock: (ip_addresses, reason = 'Bulk manual block') => api.post('/admin/monitoring/security/bulk-block', { ip_addresses, reason }),
  clearAllBlocked: () => api.post('/admin/monitoring/security/clear-all-blocked'),
  getIpLogs: (ip_address, limit = 10) => api.get(`/admin/monitoring/security/ip-logs/${ip_address}`, { params: { limit } }),
};

// TV Guide Sources API
export const tvGuideAPI = {
  getSources: () => api.get('/admin/tv-guide-sources'),
  getSource: (id) => api.get(`/admin/tv-guide-sources/${id}`),
  createSource: (source) => api.post('/admin/tv-guide-sources', source),
  updateSource: (id, source) => api.put(`/admin/tv-guide-sources/${id}`, source),
  deleteSource: (id) => api.delete(`/admin/tv-guide-sources/${id}`),
};

export default api;
